import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/modules/shared/ui/components/popover"
import {
  Command,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/modules/shared/ui/components/command"
import { userService } from "@/modules/users/infrastructure/user-service"
import { getUserRoleNames } from "@/modules/users/utils/user-roles"
import { useAddApplicationComment } from "@/modules/recruitment/applications/hooks/useApplicationComments"

const MAX_COMMENT_LENGTH = 1000
const MENTION_TRIGGER = /@([A-Za-z]*)$/

interface CommentComposerProps {
  organizationId: string
  applicationId: string
  parentCommentId?: string
  placeholder?: string
  onPosted?: () => void
  readOnly?: boolean
}

export function CommentComposer({
  organizationId,
  applicationId,
  parentCommentId,
  placeholder = "Add a note, use @ to mention someone",
  onPosted,
  readOnly = false,
}: CommentComposerProps) {
  const [text, setText] = useState("")
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const { data: usersResponse } = useQuery({
    queryKey: ["organization-users", organizationId],
    queryFn: () => userService.getOrganizationUsers(organizationId),
    enabled: mentionQuery !== null,
  })

  const addComment = useAddApplicationComment(organizationId, applicationId)

  const MENTION_ELIGIBLE_ROLES = ["OrganizationOwner", "TeamMember"]
  const filteredMembers = (usersResponse?.users ?? [])
    .filter((u) => u.status === "Accepted" && getUserRoleNames(u).some((role) => MENTION_ELIGIBLE_ROLES.includes(role)))
    .map((u) => `${u.firstName} ${u.lastName}`.trim())
    .filter((name) => name.length > 0 && name.toLowerCase().includes((mentionQuery ?? "").toLowerCase()))

  const isMentionOpen = mentionQuery !== null && filteredMembers.length > 0
  const safeHighlightedIndex = Math.min(highlightedIndex, Math.max(filteredMembers.length - 1, 0))
  const highlightedName = filteredMembers[safeHighlightedIndex]
  const filteredMembersKey = filteredMembers.join(" ")

  // Re-anchor the highlight to the top match whenever the candidate set changes,
  // so it never points at a stale/out-of-range item as the query narrows or the
  // async member list resolves.
  useEffect(() => {
    setHighlightedIndex(0)
  }, [mentionQuery, filteredMembersKey])

  const handleChange = (value: string) => {
    const trimmed = value.slice(0, MAX_COMMENT_LENGTH)
    setText(trimmed)
    const match = trimmed.match(MENTION_TRIGGER)
    setMentionQuery(match ? match[1] : null)
  }

  const selectMention = (name: string) => {
    setText((current) => current.replace(MENTION_TRIGGER, `@${name} `))
    setMentionQuery(null)
  }

  const handleSubmit = () => {
    const trimmed = text.trim()
    if (!trimmed || addComment.isPending) return

    addComment.mutate(
      { text: trimmed, parentCommentId },
      {
        onSuccess: () => {
          setText("")
          onPosted?.()
        },
      },
    )
  }

  return (
    <div className="flex gap-3">
      <div className="flex-1 relative">
        <Popover open={isMentionOpen}>
          <PopoverAnchor asChild>
            <Textarea
              value={text}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  handleSubmit()
                  return
                }

                if (isMentionOpen) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    setHighlightedIndex((i) => (i + 1) % filteredMembers.length)
                    return
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault()
                    setHighlightedIndex((i) => (i - 1 + filteredMembers.length) % filteredMembers.length)
                    return
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault()
                    selectMention(filteredMembers[safeHighlightedIndex])
                    return
                  }
                }

                if (e.key === "Escape") {
                  setMentionQuery(null)
                }
              }}
              placeholder={readOnly ? "You have read-only access to this candidate" : placeholder}
              maxLength={MAX_COMMENT_LENGTH}
              className="min-h-16 resize-y"
              disabled={addComment.isPending || readOnly}
            />
          </PopoverAnchor>
          <PopoverContent
            align="start"
            className="w-64 p-1"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command
              value={highlightedName ?? ""}
              onValueChange={(value) => {
                const idx = filteredMembers.indexOf(value)
                if (idx !== -1) setHighlightedIndex(idx)
              }}
            >
              <CommandList>
                <CommandEmpty>No matches</CommandEmpty>
                {filteredMembers.map((name) => (
                  <CommandItem key={name} value={name} onSelect={() => selectMention(name)}>
                    @{name}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            {text.length}/{MAX_COMMENT_LENGTH} &middot; Ctrl+Enter to post
          </span>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!text.trim() || addComment.isPending || readOnly}
            className="bg-primary text-primary-foreground text-sm font-medium px-4 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Post
          </button>
        </div>
      </div>
    </div>
  )
}
